import * as actionTypes from '../constants/action_types'
import * as errors from '../constants/errors'
import * as ids from '../constants/ids'
import * as wsTypes from '../modules/websockets/server/constants/action_types'
import type {LocationType} from './analysis_panel'

export type SingleClick = {
    coordinate: Array<number>,
}

export type SharedState = {
    analyzeMode: string,
    closePopup: LocationType,
    ficusNode: string,
    mapClick: SingleClick,
    mapView: {
        center: Array<number>,
        resolution: number,
        rotation: number,
    },
}

// @flow
export type CollaborationType = {
    enabled: boolean,
    fetchErrors: {
        joinSession?: {
            kind: string,
            message: string,
        },
    },
    sessions: {
        isFetching: boolean,
        items?: Array<{ token: string, name: string }>,
    },
    sharedState: {
        isFetching: boolean,
        propagateClosePopup: boolean,
        // Determines if a map click will be seen by others in the shared session
        propagateMapClicks: boolean,
        propagateMapView: boolean,
        propagateRealizationsClick: boolean,
        propagateSetAnalysisMode: boolean,
        propagateSetFicusConfig: boolean,
        propagateSetFramework: boolean,
        propagateSetTransimConfig: boolean,
        state: SharedState,
        token: string,
    },
    visible: boolean,
    form: {
        sessionName: string,
        validationErrors: {
            sessionName?: {
                type: string,
            },
        },
    },
}

const initialState = {
    enabled: false,
    fetchErrors: {},
    sessions: {
        isFetching: false,
    },
    sharedState: {
        propagateClosePopup: true,
        propagateMapClicks: true,
        propagateMapView: true,
        propagateRealizationsClick: true,
        propagateSetAnalysisMode: true,
        propagateSetFicusConfig: true,
        propagateSetFramework: true,
        propagateSetTransimConfig: true,
        state: {},
    },
    visible: true,
    form: {
        sessionName: '',
        validationErrors: {
            sessionName: {
                type: errors.NO_ERROR,
            },
        },
    },
}


/**
 * @flow
 * When a request to join a session succeeds, remove any fetch errors
 * from previous requests from the state
 */
function removeFetchJoinSessionErrors(fetchErrors: { joinSession: object }) {
    const newObj = {}
    Object.keys(fetchErrors).forEach((key) => {
        if (key !== 'joinSession') {
            newObj[key] = fetchErrors[key]
        }
    })
    return newObj
}

export default function collaboration(state = initialState, action) {
    if (action.payload) {
        console.log(`collaboration reducer: ${action.type} - ${action.payload.operation.name}`)
    }
    switch (action.type) {
    case actionTypes.CLICK_CONTEXT_BAR_ITEM:
        if (action.id === ids.COLLABORATE) {
            // Toggle the control bar if clicking an already-active framework button.
            // Otherwise, always show the control bar.
            return (action.active
                ? { ...state, visible: !state.visible }
                : { ...state, visible: true })
        }
        return state

    case actionTypes.CHANGE_INPUT:
        if (action.id === ids.COLLABORATION_SESSION_NAME) {
            return {
                ...state,
                form: {
                    ...state.form,
                    sessionName: action.value,
                },
            }
        }
        return state

    case actionTypes.FETCH_ERROR: {
        if (action.action === actionTypes.FETCH_JOIN_SESSION) {
            return {
                ...state,
                fetchErrors: {
                    joinSession: {
                        kind: action.err.kind,
                        message: action.err.exception.message,
                    },
                },
            }
        }
        return state
    }

    case actionTypes.FETCH_SESSIONS:
        return {
            ...state,
            sessions: {
                ...state.sessions,
                isFetching: action.isFetching,
            },
        }

    case actionTypes.FETCH_SESSION_STATE:
        return {
            ...state,
            isFetching: action.isFetching,
            sharedState: {
                ...state.sharedState,
                isFetching: action.isFetching,
            },
        }

    case actionTypes.HANDLED_CLICK_MAP:
        return {
            ...state,
            sharedState: {
                ...state.sharedState,
                state: {
                    ...state.sharedState.state,
                    mapClick: false,
                },
            },
        }

    case actionTypes.HANDLED_CLICK_REALIZATIONS:
        return {
            ...state,
            sharedState: {
                ...state.sharedState,
                state: {
                    ...state.sharedState.state,
                    realizationsClick: false,
                },
            },
        }

    case actionTypes.HANDLED_CLOSE_POPUP:
        return {
            ...state,
            sharedState: {
                ...state.sharedState,
                state: {
                    ...state.sharedState.state,
                    closePopup: false,
                },
            },
        }

    case actionTypes.HANDLED_SET_FICUS_CONFIG:
        return {
            ...state,
            sharedState: {
                ...state.sharedState,
                state: {
                    ...state.sharedState.state,
                    ficusConfig: false,
                },
            },
        }

    case actionTypes.HANDLED_SET_ANALYZE_MODE:
        return {
            ...state,
            sharedState: {
                ...state.sharedState,
                state: {
                    ...state.sharedState.state,
                    analyzeMode: false,
                },
            },
        }

    case actionTypes.HANDLED_SET_FRAMEWORK:
        return {
            ...state,
            sharedState: {
                ...state.sharedState,
                state: {
                    ...state.sharedState.state,
                    ficusNode: false,
                },
            },
        }

    case actionTypes.RECEIVE_JOIN_SESSION:
        return {
            ...state,
            enabled: true,
            fetchErrors: removeFetchJoinSessionErrors(state.fetchErrors),
            sharedState: {
                ...state.sharedState,
                token: action.token,
            },
        }

    case actionTypes.RECEIVE_LEAVE_SESSION:
        return {
            ...state,
            enabled: false,
            sharedState: {
                ...state.sharedState,
                token: '',
            },
        }

    case actionTypes.RECEIVE_SESSIONS:
        return {
            ...state,
            sessions: {
                ...state.sessions,
                items: action.sessions,
            },
        }

    case actionTypes.RECEIVE_SHARE_SESSION:
        return {
            ...state,
            enabled: true,
            sessions: {
                ...state.sessions,
                items: action.sessions,
            },
        }

    case actionTypes.RECEIVE_SESSION_STATE:
        return {
            ...state,
            sharedState: {
                ...state.sharedState,
                state: action.state,
            },
        }

    case actionTypes.RECEIVE_UNSHARE_SESSION:
        if (action.success) {
            const filteredSessions = state.sessions.items.filter(sess => sess.token !== action.token)
            return {
                ...state,
                enabled: false,
                sessions: {
                    ...state.sessions,
                    items: filteredSessions,
                },
            }
        }
        return state

    case actionTypes.VALIDATION_ERROR:
        if (action.id === ids.COLLABORATION_SESSION_NAME) {
            return {
                ...state,
                form: {
                    ...state.form,
                    validationErrors: {
                        ...state.form.validationErrors,
                        sessionName: {
                            type: action.error,
                        },
                    },
                },
            }
        }
        return state

    case wsTypes.WS_CLIENT_ACTION: {
        console.log(`WS_CLIENT_ACTION: ${action.payload.operation.name}`)
        if (state.enabled && action.payload.operation.name === actionTypes.OT_SHARE_SESSION) {
            // Set the shared session to this one
            const session = {
                token: action.payload.operation.params.token,
                name: action.payload.operation.params.name,
            }
            return {
                ...state,
                sessions: {
                    ...state.sessions,
                    items: [session],
                },
            }
        } else if (action.payload.operation.name === actionTypes.OT_UPDATE_MAP_VIEW) {
            return {
                ...state,
                sharedState: {
                    ...state.sharedState,
                    state: {
                        ...state.sharedState.state,
                        mapView: action.payload.operation.params,
                    },
                },
            }
        } else if (action.payload.operation.name === actionTypes.OT_CLICK_MAP) {
            return {
                ...state,
                sharedState: {
                    ...state.sharedState,
                    state: {
                        ...state.sharedState.state,
                        mapClick: action.payload.operation.params,
                    },
                },
            }
        } else if (action.payload.operation.name === actionTypes.OT_CLICK_REALIZATIONS) {
            return {
                ...state,
                sharedState: {
                    ...state.sharedState,
                    state: {
                        ...state.sharedState.state,
                        realizationsClick: action.payload.operation.params,
                    },
                },
            }
        } else if (action.payload.operation.name === actionTypes.OT_CLOSE_POPUP) {
            return {
                ...state,
                sharedState: {
                    ...state.sharedState,
                    state: {
                        ...state.sharedState.state,
                        closePopup: action.payload.operation.params,
                    },
                },
            }
        } else if (action.payload.operation.name === actionTypes.OT_SET_ANALYZE_MODE) {
            return {
                ...state,
                sharedState: {
                    ...state.sharedState,
                    state: {
                        ...state.sharedState.state,
                        analyzeMode: action.payload.operation.params.analyzeMode,
                    },
                },
            }
        } else if (action.payload.operation.name === actionTypes.OT_SET_FICUS_CONFIG) {
            return {
                ...state,
                sharedState: {
                    ...state.sharedState,
                    state: {
                        ...state.sharedState.state,
                        ficusConfig: action.payload.operation.params,
                    },
                },
            }
        } else if (action.payload.operation.name === actionTypes.OT_SET_FRAMEWORK) {
            return {
                ...state,
                sharedState: {
                    ...state.sharedState,
                    state: {
                        ...state.sharedState.state,
                        ficusNode: action.payload.operation.params.ficusNode,
                    },
                },
            }
        }
        return state
    }

    case wsTypes.WS_PRE_DISPATCH: {
        // Disable event propagation for events triggered from shared sessions
        if (action.payload.operation) {
            if (action.payload.operation.name === actionTypes.OT_CLICK_MAP) {
                console.log(`WS_PRE_DISPATCH: ${action.payload.operation.name}`)
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateMapClicks: false,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_CLICK_REALIZATIONS) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateRealizationsClick: false,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_CLOSE_POPUP) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateClosePopup: false,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_SET_ANALYZE_MODE) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateSetAnalysisMode: false,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_SET_FICUS_CONFIG) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateSetFicusConfig: false,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_SET_FRAMEWORK) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateSetFramework: false,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_UPDATE_MAP_VIEW) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateMapView: false,
                    },
                }
            }
        }
        return state
    }

    case wsTypes.WS_POST_DISPATCH: {
        // Disable event propagation for events triggered from shared sessions
        if (action.payload.operation) {
            console.log(`WS_POST_DISPATCH: ${action.payload.operation.name}`)
            if (action.payload.operation.name === actionTypes.OT_CLICK_MAP) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateMapClicks: true,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_CLICK_REALIZATIONS) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateRealizationsClick: true,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_CLOSE_POPUP) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateClosePopup: true,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_SET_ANALYZE_MODE) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateSetAnalysisMode: true,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_SET_FICUS_CONFIG) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateSetFicusConfig: true,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_SET_FRAMEWORK) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateSetFramework: true,
                    },
                }
            } else if (action.payload.operation.name === actionTypes.OT_UPDATE_MAP_VIEW) {
                return {
                    ...state,
                    sharedState: {
                        ...state.sharedState,
                        propagateMapView: true,
                    },
                }
            }
        }
        return state
    }

    default:
        return state
    }
}
